import React from 'react';
import { Box, Divider } from '@withjoy/joykit';
import { useNativeState } from '@shared/core/native';
import { useCatalogController } from './Catalog.controller';
import { Redirect, Route, Switch } from '@react-router';
import { RegistryFontFaces } from '@apps/registry/common/util/fonts';
import { useCatalogRoutePaths } from '@apps/registry/common/components/Catalog/Catalog.routes';
import type { RegistryCurrencyCodesFragment } from '@graphql/fragments/currency.graphql.generated';
import { useFeatureValue } from '@shared/core/featureFlags';
import { BackToTop } from '@shared/components/BackToTop';
import { MegaNav } from './components/MegaNav';
import { ScrollToTop } from '@shared/components/ScrollToTop/ScrollToTop';
import { pxToRem } from '@withjoy/joykit/theme';
import { DESKTOP_NAV_HEIGHT } from '@shared/components/Marcom/AppNavBar/AppNavBar.constants';
import { DepartmentsFooter } from './components/DepartmentsFooter/DepartmentsFooter';
import { LazyCatalogProductDetails } from './routes/CatalogProductDetails/CatalogProductDetails.lazy';
import { LazyCurationDetails } from './routes/CurationDetails/CurationDetails.lazy';
import { LazyCatalogProductsIndex } from './routes/CatalogProductsIndex/CatalogProductsIndex.lazy';
import { LazyCurationsIndex } from './routes/CurationsIndex/CurationsIndex.lazy';
import { LazyBrandPage } from './routes/BrandPage/BrandPage.lazy';
import { LazyBrandsList } from './routes/BrandsList/BrandsList.lazy';
import { LazyShippingPolicy } from './routes/ShippingPolicy/ShippingPolicy.lazy';
import { LazyReturnPolicy } from './routes/ReturnPolicy/ReturnPolicy.lazy';

interface CatalogProps {
  heroContent?: React.ReactNode;
  registryCurrency?: RegistryCurrencyCodesFragment | null;
}

const Catalog: React.FC<CatalogProps> = ({ heroContent }) => {
  const { isMobile, getRootCatalogPath } = useCatalogController();

  const isCurationHomeExpertFeatureEnabled = useFeatureValue('enableCurationHomeExpert');

  const { catalogRoutes } = useCatalogRoutePaths();

  return (
    <>
      <Switch>
        <Route
          path={catalogRoutes.shippingPolicy.paths}
          render={() => {
            return <LazyShippingPolicy />;
          }}
        />
        <Route
          path={catalogRoutes.returnPolicy.paths}
          render={() => {
            return <LazyReturnPolicy />;
          }}
        />
        <Route
          path={catalogRoutes.curationDetails.paths}
          render={({ match, staticContext }) => {
            return match.params.elementId ? (
              <>
                <LazyCurationDetails
                  curationLocator={match.params.elementId}
                  isMobile={isMobile}
                  staticContext={staticContext}
                />
                <BackToTop />
              </>
            ) : (
              <Redirect to="/shop" />
            );
          }}
        />
        <Route
          path={catalogRoutes.curationsIndex.paths}
          render={() => {
            return <LazyCurationsIndex />;
          }}
        />
        <Route
          path={catalogRoutes.brandsList.paths}
          exact={true}
          render={() => <LazyBrandsList />}
        />
        <Route
          path={catalogRoutes.brandPage.paths}
          render={({ match }) => {
            return match.params.elementId ? (
              <>
                <LazyBrandPage
                  brandLocator={match.params.elementId}
                  isMobile={isMobile}
                />
                <BackToTop />
              </>
            ) : (
              <Redirect to="/shop" />
            );
          }}
        />
        <Route
          path={[...catalogRoutes.catalogIndex.paths, ...catalogRoutes.recommendedProducts.paths]}
          exact={true}
          render={() => {
            return (
              <>
                <LazyCatalogProductsIndex
                  categoryLocator={null}
                  heroContent={isCurationHomeExpertFeatureEnabled ? <></> : heroContent}
                />

                <BackToTop />
              </>
            );
          }}
        />
        <Route
          path={[...catalogRoutes.catalogCategory.paths]}
          render={({ match, staticContext }) => {
            return (
              <>
                <LazyCatalogProductsIndex
                  staticContext={staticContext}
                  categoryLocator={match.params.elementId}
                  heroContent={isCurationHomeExpertFeatureEnabled ? <></> : heroContent}
                />

                <BackToTop />
              </>
            );
          }}
        />

        {/* Redirect to shop root if no valid route matches */}
        <Redirect to={getRootCatalogPath()} />
      </Switch>
    </>
  );
};
Catalog.displayName = 'Catalog';

interface CatalogContainerProps {
  heroContent?: React.ReactNode;
  header?: React.ReactNode;
  registryCurrency?: RegistryCurrencyCodesFragment | null;
  hideMegaNav?: boolean;
}

const CatalogContainer: React.FC<CatalogContainerProps> = props => {
  const { isNative } = useNativeState();
  const { catalogRoutes } = useCatalogRoutePaths();

  return (
    <>
      <RegistryFontFaces />
      {props.header}
      {!props.hideMegaNav && (
        <>
          <Box
            position="relative"
            zIndex={20}
          >
            <MegaNav />
          </Box>
          <Divider
            top={[pxToRem(66 + (isNative ? 52 : 0)), null, pxToRem(DESKTOP_NAV_HEIGHT)]}
            zIndex={6}
            position="sticky"
          />
        </>
      )}
      <Switch>
        <Route
          path={catalogRoutes.catalogItem.paths}
          render={({ match, staticContext }) => {
            return match && match.params.elementId ? (
              <>
                <ScrollToTop behavior="instant" />
                <LazyCatalogProductDetails
                  catalogItemId={match.params.elementId}
                  staticContext={staticContext}
                />
              </>
            ) : (
              <Redirect to="/shop" />
            );
          }}
        />
        <Route
          render={() => {
            return <Catalog {...props} />;
          }}
        />
      </Switch>
      {!isNative && <DepartmentsFooter />}
    </>
  );
};

export { CatalogContainer };
